/* eslint-disable prefer-arrow/prefer-arrow-functions */
import { useEffect } from "react";

import type { To } from "@shared/react-router-dom";
import { useLocation, useNavigate } from "@shared/react-router-dom";

interface Options {
  pathname?: string;
  replace?: boolean;
  preserveScroll?: boolean;
}

interface LocationState {
  scrollPosition: number;
}

const ARRAY_SUFFIX_INDICATOR = "_array";

function getParamsString<T>(paramObject: T): string {
  const urlParams = new URLSearchParams();
  const filteredParamsEntries = Object.entries(paramObject).filter(entry => entry[1] !== undefined);

  filteredParamsEntries.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(subValue => {
        urlParams.append(key + ARRAY_SUFFIX_INDICATOR, JSON.stringify(subValue));
      });
    } else {
      urlParams.append(key, JSON.stringify(value));
    }
  });

  return urlParams.toString();
}

const tryParse = value => {
  try {
    return JSON.parse(value);
  } catch {
    // In case it's just a string and not in JSON format
    return value;
  }
};

function parseParamsString<T>(paramString: string): T {
  const urlParams = new URLSearchParams(paramString);
  const paramObject = {};

  const isArrayParam = (key: string) => key.endsWith(ARRAY_SUFFIX_INDICATOR);
  const removeArrayIndicator = (key: string) =>
    key.slice(0, key.length - ARRAY_SUFFIX_INDICATOR.length);

  urlParams.forEach((value, key) => {
    if (isArrayParam(key)) {
      const parsedKey = removeArrayIndicator(key);
      const currentArray = paramObject[parsedKey] || [];

      paramObject[parsedKey] = [...currentArray, JSON.parse(value)];
    } else if (key in paramObject && !Array.isArray(paramObject[key])) {
      paramObject[key] = [paramObject[key], tryParse(value)];
    } else if (key in paramObject) {
      paramObject[key] = [...paramObject[key], tryParse(value)];
    } else {
      paramObject[key] = tryParse(value);
    }
  });

  return paramObject as T;
}

function useUrlParams<T extends Record<string, any>>(
  initialOptions: Options = {}
): [T, (params: T, options?: Options) => void] {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = location.search;

  const { scrollPosition } = (location?.state as LocationState) ?? {};

  useEffect(() => {
    if (scrollPosition) {
      window.scrollTo(0, (location.state as LocationState).scrollPosition);
      (location.state as LocationState).scrollPosition = undefined;
    }
  }, [scrollPosition]);

  const setUrlParams = (params: T, options?: Options): void => {
    const defaultOptions = {
      pathname: location.pathname,
      replace: false,
      preserveScroll: false,
    };

    const { pathname, replace, preserveScroll } = {
      ...defaultOptions,
      ...initialOptions,
      ...options,
    };

    const newLocation: To = {
      pathname,
      search: getParamsString<T>(params),
    };

    navigate(newLocation, {
      replace,
      state: preserveScroll ? { scrollPosition: window.pageYOffset } : undefined,
    });
  };

  return [parseParamsString<T>(searchParams), setUrlParams];
}

export type { Options as ParamsOptions };
export { useUrlParams, parseParamsString, getParamsString };
